import useGameStore, {
  districtSetOf,
  districtsHeldBy,
  floatSharesOf,
  ownershipPct,
  rentFor,
  rentYieldOf,
} from '../store/gameStore';
import type { MarketPhase, Player, PropertyTile } from '../types/gameTypes';
import { money, percent } from '../lib/format';
import { tileName as localizeTile, useT } from '../i18n';
import { DISTRICT_META } from './districtMeta';

/**
 * Compact cap-table widgets used by the sidebar, the tile inspector and the district
 * summary: one segmented ownership bar (player colours + gray public float) that is
 * always visible, plus hover tooltips with the exact share counts.
 */

export interface EquitySegment {
  id: string;
  name: string;
  color: string;
  shares: number;
  /** Percentage of the issued shares (0–100). */
  pct: number;
  isFloat: boolean;
}

/** Split a tile's cap table into coloured segments (+ the gray unowned float). */
export const equitySegmentsOf = (tile: PropertyTile, players: Player[]): EquitySegment[] => {
  const total = Math.max(1, tile.totalShares);
  const float = floatSharesOf(tile);
  const segments: EquitySegment[] = players
    .map((player) => ({
      id: player.id,
      name: player.name,
      color: player.color,
      shares: tile.shares[player.id] ?? 0,
      pct: ((tile.shares[player.id] ?? 0) / total) * 100,
      isFloat: false,
    }))
    .filter((segment) => segment.shares > 0)
    .sort((a, b) => b.shares - a.shares);
  if (float > 0) {
    segments.push({
      id: '__float',
      name: 'Public float',
      color: '#475569',
      shares: float,
      pct: (float / total) * 100,
      isFloat: true,
    });
  }
  return segments;
};

/** Aggregate a whole colour group: every player's share of the district's equity. */
export const districtEquitySegmentsOf = (tiles: PropertyTile[], players: Player[]): EquitySegment[] => {
  const issued = tiles.reduce((sum, tile) => sum + Math.max(1, tile.totalShares), 0);
  if (issued <= 0) return [];
  const segments: EquitySegment[] = players
    .map((player) => {
      const shares = tiles.reduce((sum, tile) => sum + (tile.shares[player.id] ?? 0), 0);
      return { id: player.id, name: player.name, color: player.color, shares, pct: (shares / issued) * 100, isFloat: false };
    })
    .filter((segment) => segment.shares > 0)
    .sort((a, b) => b.shares - a.shares);
  const float = tiles.reduce((sum, tile) => sum + floatSharesOf(tile), 0);
  if (float > 0) {
    segments.push({ id: '__float', name: 'Public float', color: '#475569', shares: float, pct: (float / issued) * 100, isFloat: true });
  }
  return segments;
};

/** Segmented multi-coloured ownership bar: one slice per shareholder + gray float. */
export const EquityBar = ({
  segments,
  height = 'h-2',
  className = '',
}: {
  segments: EquitySegment[];
  height?: string;
  className?: string;
}) => {
  if (segments.length === 0) return <span className={`${height} block w-full rounded-full bg-slate-800 ${className}`} />;
  return (
    <span className={`flex ${height} w-full overflow-hidden rounded-full bg-slate-800 ${className}`}>
      {segments.map((segment) => (
        <span
          key={segment.id}
          title={`${segment.name}: ${segment.shares} shares (${segment.pct.toFixed(1)} %)`}
          style={{ width: `${segment.pct}%`, backgroundColor: segment.color }}
          className="h-full"
        />
      ))}
    </span>
  );
};

/** Hover-able sub-label with the exact share counts of the biggest holders. */
export const EquityLegend = ({ segments, limit = 3 }: { segments: EquitySegment[]; limit?: number }) => {
  if (segments.length === 0) return null;
  const shown = segments.slice(0, limit);
  const rest = segments.slice(limit);
  const restPct = rest.reduce((sum, segment) => sum + segment.pct, 0);
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[9px] leading-tight text-slate-400">
      {shown.map((segment) => (
        <span key={segment.id} title={`${segment.shares} shares`} className="flex items-center gap-0.5">
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: segment.color }} />
          {segment.isFloat ? 'Float' : segment.name.split(' ')[0]} {segment.pct.toFixed(0)}%
        </span>
      ))}
      {restPct > 0.5 && (
        <span title={`${rest.map((s) => `${s.name} ${s.shares}`).join(', ')}`}>Others {restPct.toFixed(0)}%</span>
      )}
    </p>
  );
};


/**
 * COMPACT portfolio: 3–4 districts fit without deep scrolling, every property card
 * carries a persistent equity bar and the live rent / yield readout.
 */
export const DistrictPortfolio = () => {
  const store = useGameStore();
  const t = useT();
  const { board, players, currentPlayerIndex, turnPhase, market } = store;
  const active = players[currentPlayerIndex];
  const human: Player | undefined = active && !active.isAI ? active : players[0];
  const isHumanTurn =
    Boolean(active && !active.isAI) &&
    (turnPhase === 'GOVERNANCE_PHASE' || turnPhase === 'TURN_END' || turnPhase === 'TILE_RESOLUTION' || turnPhase === 'TURN_START');
  const holdings = human ? board.filter((tile) => (tile.shares[human.id] ?? 0) > 0) : [];
  const districts = human ? districtsHeldBy(board, human.id) : [];

  return (
    <div className="glass rounded-xl p-2">
      <h2 className="mb-1.5 flex items-center justify-between text-[11px] font-semibold uppercase tracking-widest text-slate-400">
        <span>{t('portfolio.title')}</span>
        {holdings.length > 0 && (
          <span className="rounded-full bg-slate-800/80 px-1.5 py-0.5 text-[10px] text-slate-300">
            {t('portfolio.districtsCount', { count: districts.length })}
          </span>
        )}
      </h2>
      {!human || holdings.length === 0 ? (
        <p className="text-xs text-slate-400">{t('portfolio.empty')}</p>
      ) : (
        <div className="max-h-[34vh] space-y-1.5 overflow-y-auto pr-1 scrollbar-thin">
          {districts.map((district) => {
            const set = districtSetOf(board, human.id, district);
            const meta = DISTRICT_META[district];
            const aggregate = districtEquitySegmentsOf(set.tiles, players);
            return (
              <div key={district} className="rounded-lg border p-2" style={{ borderColor: `${meta.bar}66`, backgroundColor: `${meta.bar}10` }}>
                <div className="flex items-center justify-between gap-1.5">
                  <span className="flex min-w-0 items-center gap-1 text-xs font-semibold" style={{ color: meta.bar }}>
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: meta.bar }} />
                    <span className="truncate">{t('portfolio.district', { name: meta.short })}</span>
                  </span>
                  {set.complete ? (
                    <span className="shrink-0 rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-black text-emerald-300">{t('common.monopolyBonus')}</span>
                  ) : (
                    <span className="shrink-0 rounded-full bg-slate-800/80 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-200">
                      {t('common.ownedBadge', { owned: set.owned, total: set.total })}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-1.5" title={t('portfolio.districtSize', { total: set.total })}>
                  <EquityBar segments={aggregate} height="h-1.5" className="flex-1" />
                  <span className="shrink-0 font-mono text-[9px] font-bold text-slate-300">{set.pct.toFixed(0)} %</span>
                </div>
                <div className="mt-1 space-y-1">
                  {set.tiles.map((tile) => (
                    <DistrictPropertyRow
                      key={tile.id}
                      tile={tile}
                      players={players}
                      human={human}
                      canAct={isHumanTurn}
                      phase={market.phase}
                      onInspect={() => store.setInspectTile(board.findIndex((entry) => entry.id === tile.id))}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** One compact property line: cap-table bar + live rent/yield + inline action. */
const DistrictPropertyRow = ({
  tile,
  players,
  human,
  canAct,
  phase,
  onInspect,
}: {
  tile: PropertyTile;
  players: Player[];
  human: Player;
  canAct: boolean;
  phase: MarketPhase;
  onInspect: () => void;
}) => {
  const store = useGameStore();
  const t = useT();
  const pct = ownershipPct(tile, human.id);
  const segments = equitySegmentsOf(tile, players);

  if (pct <= 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-600/50 px-1.5 py-1">
        <p className="flex items-center justify-between text-[10px] text-slate-500">
          <span className="truncate">□ {localizeTile(tile.name, t)}</span>
          <span className="shrink-0">0 %</span>
        </p>
        <EquityBar segments={segments} height="h-1.5" className="mt-1" />
        <EquityLegend segments={segments} limit={2} />
      </div>
    );
  }

  const isMajority = pct > 50;
  const cost = Math.round(tile.upgradeCost * (tile.upgradeLevel + 1));
  const canUpgrade = human.cash >= cost && tile.upgradeLevel < 5 && canAct && !tile.isFrozen;
  const liveRent = rentFor(tile, phase);
  return (
    <div className={`rounded-md border px-1.5 py-1 ${isMajority ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-slate-700/60 bg-slate-950/50'}`}>
      <div className="flex items-center justify-between gap-1 text-[10px]">
        <span className="min-w-0 truncate font-semibold text-slate-100">
          {isMajority ? '■' : '▣'} {localizeTile(tile.name, t)}
        </span>
        <span className="shrink-0 font-mono text-slate-400">
          {pct.toFixed(0)}% · T{tile.upgradeLevel}
        </span>
      </div>
      <EquityBar segments={segments} height="h-2" className="mt-1" />
      <EquityLegend segments={segments} limit={2} />
      <p className="mt-0.5 flex items-center justify-between gap-1 text-[9px] text-slate-400">
        <span className="truncate">
          {t('portfolio.rentLine', { rent: money(liveRent), yield: percent(rentYieldOf(tile) * 100, 1) })}
        </span>
        <span className="shrink-0 text-emerald-300">{t('common.yourCut', { amount: money((liveRent * pct) / 100) })}</span>
      </p>
      <div className="mt-1 flex gap-1">
        <button
          type="button"
          disabled={!canUpgrade}
          onClick={() => store.proposeUpgrade(tile.id, human.id, tile.upgradeLevel + 1)}
          className="min-h-[28px] flex-1 rounded-md border border-cyan-400/60 bg-cyan-600/80 px-2 py-0.5 text-[10px] font-bold text-white transition hover:bg-cyan-500 disabled:cursor-not-allowed disabled:border-slate-700 disabled:bg-slate-800 disabled:text-slate-500"
        >
          {tile.isFrozen ? t('common.frozen') : isMajority ? `🏗 ${t('common.upgrade')} ${money(cost)}` : `🗳 ${t('common.vote')} ${money(cost)}`}
        </button>
        <button
          type="button"
          onClick={onInspect}
          className="min-h-[28px] rounded-md border border-slate-600 bg-slate-800 px-2 py-0.5 text-[10px] font-bold text-slate-200 hover:bg-slate-700"
        >
          {t('common.inspect')}
        </button>
      </div>
    </div>
  );
};

export default DistrictPortfolio;
